import { Fragment } from 'react';

/**
 * ─── InstructionText ─────────────────────────────────────────────────────────
 *
 * Rendu « type examen CKA/CKAD/CKS » d'une consigne : reproduit la structure des
 * énoncés réels — noms/valeurs/commandes en CHIPS de code (`backticks`),
 * spécifications en PUCES (`- `), et blocs de commande (```). Rendu pour le
 * panneau clair du lab (fond blanc). Pas de dépendance markdown externe.
 */

/** Rend le texte inline avec des chips de code pour les portions `entre backticks`. */
function renderInline(text: string, keyBase: string) {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('`') && p.endsWith('`') && p.length > 1) {
      return (
        <code
          key={`${keyBase}-c${i}`}
          className="mx-0.5 rounded-md bg-blue-100 px-1.5 py-0.5 font-mono text-[0.85em] text-blue-700 ring-1 ring-blue-200"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={`${keyBase}-t${i}`}>{p}</Fragment>;
  });
}

export function InstructionText({ text, className = '' }: { text: string; className?: string }) {
  // 1) Découpe en segments : blocs de code ``` … ``` vs texte.
  const segments = text.split(/```(?:[a-zA-Z0-9]*)\n?([\s\S]*?)```/g);
  const out: React.ReactNode[] = [];

  segments.forEach((seg, si) => {
    // Les indices IMPAIRS sont le contenu capturé des blocs de code.
    if (si % 2 === 1) {
      out.push(
        <pre
          key={`code-${si}`}
          className="my-3 overflow-x-auto rounded-lg bg-slate-900 px-3.5 py-3 text-[13px] leading-relaxed text-slate-100"
        >
          <code className="font-mono whitespace-pre">{seg.replace(/\n$/, '')}</code>
        </pre>,
      );
      return;
    }
    if (!seg.trim()) return;

    // 2) Texte : regroupe les lignes en paragraphes et listes à puces.
    const lines = seg.split('\n');
    let bullets: string[] = [];
    const flush = () => {
      if (!bullets.length) return;
      out.push(
        <ul key={`ul-${si}-${out.length}`} className="my-2 ml-1 space-y-1.5">
          {bullets.map((b, bi) => (
            <li key={bi} className="flex gap-2 text-slate-800">
              <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
              <span className="leading-relaxed">{renderInline(b, `b${si}-${bi}`)}</span>
            </li>
          ))}
        </ul>,
      );
      bullets = [];
    };

    lines.forEach((raw, li) => {
      const line = raw.trimEnd();
      const m = line.match(/^\s*[-*]\s+(.*)$/);
      if (m) {
        bullets.push(m[1]);
      } else if (line.trim() === '') {
        flush();
      } else {
        flush();
        out.push(
          <p key={`p-${si}-${li}`} className="leading-relaxed text-slate-800">
            {renderInline(line, `p${si}-${li}`)}
          </p>,
        );
      }
    });
    flush();
  });

  return <div className={`space-y-1.5 text-[15px] ${className}`}>{out}</div>;
}
